import { batch } from 'react-redux';
import { hasListener } from '@client/selectors/helpers/listeners';
import { startFetchPublicShowDetails, startFetchShowDetails, unsubscribeShow } from '@client/slices/entities/shows.reducer';
import { addFirestoreListener, removeFirestoreListener } from '@client/slices/helpers/listeners.reducer';
import { fetchShowTickets } from '@client/listeners/utils/tickets';
import { getUserId, getSessionUserAdminStatus, getCurrentPosition } from '@client/selectors/helpers/session';
import { startFetchShowProducts, unsubscribeShowProducts } from '@client/slices/entities/products.reducer';
import { setCurrentSKU } from '@client/slices/features/stripe.reducer';
import { setLoaderIsFinishedError, setLoaderIsFinishedSuccess, setLoaderIsLoading } from '@client/slices/helpers/loaders.reducer';
import { getDateTime } from '@sda/utils/date';
import { showError } from '@client/listeners/lib/alerts';
import { firebaseRequest, handleRequest } from '@client/listeners/lib/requests';
import type { RootState, AppDispatch } from '@client/store';
import type { AddListener } from '@client/listeners/lib';
import type { ListenerEffectAPI } from '@reduxjs/toolkit';
import type { SKU } from '@sda/interfaces/reducer/stripe.reducer';

const fetchStripeSKU = async (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
  showId: string,
) => {
  const state = listenerApi.getState();
  const { dispatch } = listenerApi;

  const scope = `fetch-${showId}-SKU`;
  dispatch(setLoaderIsLoading(scope));

  const position = getCurrentPosition(state);

  try {
    const request = await firebaseRequest({
      url: `${process.env.API_BASE_URL}/stripe/sku/${showId}`,
      method: 'POST',
      body: {
        position,
      },
    });

    const response = await handleRequest<SKU>(request);

    if (response.status) {
      batch(() => {
        dispatch(setCurrentSKU(response.data));
        dispatch(setLoaderIsFinishedSuccess(scope));
      });
    } else {
      showError(response.error);
      dispatch(setLoaderIsFinishedError(scope, response.error));
    }
  } catch (e) {
    showError(e);
    dispatch(setLoaderIsFinishedError(scope, e));
  }
};

export const withShowListeners = (addListener: AddListener) => [
  addListener({
    actionCreator: startFetchPublicShowDetails,
    effect: (action, listenerApi) => {
      const showId = action.payload;
      const firebase = window.Firebase;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      firebase.SDK.Shows.listen(showId, null, { forceNewListener: true, modifier: { publicFetched: true } });

      const productsListenerName = `list-show-products-${showId}`;
      const productsListener = hasListener(state, productsListenerName);

      if (!productsListener) {
        const watchListener = firebase.SDK.Products.watch(
          {
            queries: (query) => [
              query.isEqualTo('showId', showId),
            ],
          },
        );

        dispatch(addFirestoreListener({ watchListener, name: productsListenerName }));
      }

      const profileListenerName = `list-show-profiles-${showId}`;
      const profileListener = hasListener(state, profileListenerName);

      if (!profileListener) {
        const watchListener = firebase.SDK.Shows.PublicProfiles(showId).watch({});

        dispatch(addFirestoreListener({ watchListener, name: profileListenerName }));
      }
    },
  }),

  addListener({
    actionCreator: startFetchShowDetails,
    effect: async (action, listenerApi) => {
      const showId = action.payload;
      const firebase = window.Firebase;

      const state = listenerApi.getState();
      const { dispatch } = listenerApi;

      const userId = getUserId(state);
      const isAdmin = getSessionUserAdminStatus(state);

      const initialShow = await firebase.SDK.Shows.get(showId);

      if (!initialShow) {
        return;
      }

      dispatch(startFetchShowProducts(showId));

      const fetchSku = (
        initialShow.stripeSKU
        && initialShow.isHyperwalletShow
        && !initialShow.isCancelled
        && !initialShow.isIncomplete
      );

      const isCollaborator = (isAdmin || (initialShow && initialShow.collaborators && initialShow.collaborators.indexOf(userId) !== -1));

      const fetchTickets = (
        fetchSku && (isCollaborator || getDateTime(initialShow.announceTime).startOf('day') <= getDateTime())
      );

      firebase.SDK.Shows.listen(showId, null, { forceNewListener: true, modifier: { publicFetched: true } });

      const profileListenerName = `list-show-profiles-${showId}`;
      const profileListener = hasListener(state, profileListenerName);

      if (!profileListener) {
        const watchListener = firebase.SDK.Shows.PublicProfiles(showId).watch({});

        dispatch(addFirestoreListener({ watchListener, name: profileListenerName }));
      }

      listenerApi.fork(async () => {
        await Promise.all([
          fetchSku && fetchStripeSKU(listenerApi, showId),
          fetchTickets && fetchShowTickets(listenerApi, initialShow),
        ]);
      });
    },
  }),

  addListener({
    actionCreator: unsubscribeShow,
    effect: (action, listenerApi) => {
      const showId = action.payload;
      const firebase = window.Firebase;
      const { dispatch } = listenerApi;

      firebase.SDK.Shows.unsubscribe(showId);
      firebase.SDK.Shows.Private().unsubscribe(showId);
      firebase.SDK.Shows.Metadata(showId).Tickets().unsubscribe(null);
      firebase.SDK.Shows.Metadata(showId).Orders().unsubscribe(null);

      const adminTicketListener = `list-ticket-${showId}`;
      dispatch(removeFirestoreListener(adminTicketListener));

      const adminRelatedTicketListener = `list-related-tickets-${showId}`;
      dispatch(removeFirestoreListener(adminRelatedTicketListener));

      const ticketListener = `list-public-ticket-${showId}`;
      dispatch(removeFirestoreListener(ticketListener));

      const affiliatesListener = `list-show-affiliates-${showId}`;
      dispatch(removeFirestoreListener(affiliatesListener));

      const profileListener = `list-show-profiles-${showId}`;
      dispatch(removeFirestoreListener(profileListener));

      const privateProfileListener = `list-show-private-profiles-${showId}`;
      dispatch(removeFirestoreListener(privateProfileListener));

      dispatch(unsubscribeShowProducts(showId));
    },
  }),
];
